// FollowupThinking — the short waiting animation shown between a base answer
// and a possible AI follow-up. Deliberately brief, deliberately literate:
//   "Listening…" → "Looking at what you just said…" → "Thinking…"
// The pass cycles every 1.8s. Total expected wait is 2–5s; the copy is tuned
// to make even a 5s wait feel intentional rather than hung.

import { useEffect, useState } from 'react'
import { getStoredLang } from '@/lib/api'
import { t as tr } from '@/lib/i18n'

interface Props {
  label?: string
}

export function FollowupThinking({ label }: Props) {
  const [i, setI] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % 3), 1800)
    return () => clearInterval(id)
  }, [])

  const lang = getStoredLang()
  const stages = [
    tr(lang, 'ai_listening_stage_1'),
    tr(lang, 'ai_listening_stage_2'),
    tr(lang, 'ai_listening_stage_3'),
  ]
  const text = label ?? stages[i]

  return (
    <section
      role="status"
      aria-live="polite"
      aria-label="Checking for a follow-up question"
      className="ml-6 pl-4 border-l-2 border-vermillion/30 py-2 space-y-2 animate-in fade-in slide-in-from-left-2 duration-300"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-vermillion/70 font-mono">
        <span className="inline-flex gap-0.5" aria-hidden="true">
          <Dot delay="0ms" />
          <Dot delay="150ms" />
          <Dot delay="300ms" />
        </span>
        <span>{tr(lang, 'ai_is_listening')}</span>
      </div>
      <p className="font-serif text-sm italic text-muted-foreground transition-opacity">
        {text}
      </p>
    </section>
  )
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="inline-block w-1 h-1 rounded-full bg-vermillion animate-pulse"
      style={{ animationDelay: delay }}
    />
  )
}

export default FollowupThinking
